import { QueueItem } from '@/api/callcenter/dashboard';
import { Badge } from '@/components/ui/elements/badge';
import { Button } from '@/components/ui/elements/button';
import { Card } from '@/components/ui/elements/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/elements/tooltip';
import { BucketBadge } from '@/features/callcenter/components/BucketBadge';
import { QueueCardDetails } from '@/features/callcenter/components/QueueCardDetails';
import { SlaCountdown } from '@/features/callcenter/components/SlaCountdown';
import { BrightPatternStatus } from '@/features/callcenter/lib/brightpattern';
import { SlaDisplay } from '@/features/callcenter/logic';
import { cn } from '@/utils/format';
import { PhoneArrowDownLeftIcon } from '@heroicons/react/24/outline';

const DIAL_UNAVAILABLE_COPY = 'Click-to-call unavailable — dial from BrightPattern';

type QueueCardProps = {
  item: QueueItem;
  display: SlaDisplay;
  onOpen?: (item: QueueItem) => void;
  onRelease?: (item: QueueItem) => void;
  onDial?: (item: QueueItem) => void;
  dialStatus?: BrightPatternStatus;
  isPending?: boolean;
  isHighlighted?: boolean;
};

// Not memoized: QueueList derives a brand-new `display` object every render
// (deriveSlaDisplay returns a fresh object literal, called every second from
// useQueueCountdowns' elapsedSeconds ticker), so a shallow-prop memo here
// would never actually skip a re-render -- it would only add a comparison
// that always fails. A false promise, not an optimization.
export const QueueCard = ({
  item,
  display,
  onOpen,
  onRelease,
  onDial,
  dialStatus,
  isPending = false,
  isHighlighted = false,
}: QueueCardProps) => {
  const cityState =
    item.lead?.city && item.lead?.state ? `${item.lead.city}, ${item.lead.state}` : null;

  // Externally held leads are being worked by the local franchise owner --
  // they stay inert even when the screen wires up onOpen, so an agent can
  // never accidentally claim one out from under them.
  const isActionable = Boolean(onOpen) && !item.externally_held;

  const handleOpen = () => {
    if (isPending) return;
    onOpen?.(item);
  };

  const showReleaseButton = item.state === 'accepted' && Boolean(onRelease);

  const handleRelease = () => {
    onRelease?.(item);
  };

  // Gated on onDial being provided (not just item.dial) so a caller that
  // never wires click-to-call -- most existing QueueCard renders, including
  // every test that predates this feature -- never grows a phantom button,
  // matching how showReleaseButton gates on onRelease above. Also requires a
  // real phone_e164 (not just a truthy `dial` object): a phone system can't
  // dial a number it doesn't have, and hiding the button entirely -- the
  // same treatment as no dial payload at all -- beats showing a control that
  // silently no-ops on click. useBrightPatternDial.dial() keeps its own
  // phone_e164 guard too, as defense in depth for any future caller that
  // reaches it without going through this button.
  const showDialButton = Boolean(item.dial?.phone_e164) && !item.externally_held && Boolean(onDial);
  const dialReady = dialStatus === 'ready';

  const handleDial = () => {
    onDial?.(item);
  };

  // Shared between the ready (enabled) and not-ready (disabled, tooltipped)
  // branches below so the two never drift out of sync with each other --
  // same reasoning as the shared `content` block above.
  const dialButton = (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      disabled={!dialReady}
      aria-label={`Call ${item.lead?.name ?? 'Unknown lead'}`}
      onClick={handleDial}
    >
      <PhoneArrowDownLeftIcon className="h-4 w-4" aria-hidden="true" />
    </Button>
  );

  // Shared between the actionable <button> and the plain, inert <div> below
  // so the two branches never drift out of sync with each other.
  const content = (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <span data-testid="lead-name" className="font-semibold text-foreground">
          {item.lead?.name ?? 'Unknown lead'}
        </span>
        {item.state === 'accepted' && (
          <Badge variant="green" size="xs">
            In progress
          </Badge>
        )}
      </div>

      {item.lead?.phone && <div className="text-sm text-muted-foreground">{item.lead.phone}</div>}
      {cityState && <div className="text-xs text-muted-foreground">{cityState}</div>}

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <BucketBadge bucket={item.bucket} />
        {item.rotation_count > 0 && (
          <span className="text-xs text-muted-foreground">Reassigned ×{item.rotation_count}</span>
        )}
      </div>
    </>
  );

  return (
    <Card
      data-testid="queue-card"
      className={cn(
        'flex flex-col gap-2',
        isPending && 'opacity-70',
        // A realtime nudge (accept-expiring / idle / SLA warning) highlights
        // its row for a few seconds -- a visible accent, not just a toast,
        // since the agent may not be looking at the toast corner.
        isHighlighted && 'ring-2 ring-primary animate-pulse',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        {isActionable ? (
          // The PRIMARY open affordance: a real <button> wrapping the name/meta
          // block, as a direct sibling of Release below -- never nested inside
          // it or vice versa. Enter/Space activation is native (no manual
          // onKeyDown needed, unlike the old div[role=button]).
          <button
            type="button"
            aria-label={`Open ${item.lead?.name ?? 'Unknown lead'}`}
            aria-busy={isPending}
            onClick={handleOpen}
            className="min-w-0 cursor-pointer space-y-1 text-left"
          >
            {content}
          </button>
        ) : (
          <div className="min-w-0 space-y-1">{content}</div>
        )}

        <div className={cn('shrink-0 text-right space-y-2', item.externally_held && 'max-w-40')}>
          {item.externally_held ? (
            <span className="text-sm text-muted-foreground">Being worked by the local owner</span>
          ) : (
            <SlaCountdown display={display} />
          )}
          {(showDialButton || showReleaseButton) && (
            <div className="flex items-center justify-end gap-1">
              {showDialButton &&
                (dialReady ? (
                  dialButton
                ) : (
                  <TooltipProvider delayDuration={50}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {/* Radix's trigger listens for pointer/focus events on
                          this element -- the Button itself gets
                          pointer-events-none from Tailwind's disabled:
                          styling once `disabled` is set, which would
                          otherwise stop the browser from ever routing hover
                          to it at all. Wrapping it in a plain span keeps the
                          hover target reachable while the button stays
                          genuinely disabled (no click, no keyboard
                          activation). */}
                        <span tabIndex={-1} className="inline-flex">
                          {dialButton}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{DIAL_UNAVAILABLE_COPY}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ))}
              {showReleaseButton && (
                <Button type="button" variant="ghost" size="sm" onClick={handleRelease}>
                  Release
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Outside the open button on purpose: a block this size does not belong
          inside a control, and keeping it inert is what guarantees the queue
          stays a place to triage rather than a place to work a lead. */}
      {item.lead && <QueueCardDetails lead={item.lead} />}
    </Card>
  );
};
